import {
  confirmAdmissionImport,
  downloadPrefilledAdmissionTemplate,
  previewAdmissionImport,
  getRemainingAdmissionVerification,
  verifyRemainingAdmissions,
  type RemainingAdmissionVerification,
  type AdmissionImportPreview,
} from '@urbanik/api-client';
import { Button, CanonicalModal, InfoPanel, StatusBadge } from '@urbanik/ui';
import { FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { apiBase } from './api-base';
import './member-admission-bulk.css';
export function MemberAdmissionBulkImport({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false),
    [csv, setCsv] = useState<File | null>(null),
    [pdf, setPdf] = useState<File | null>(null),
    [preview, setPreview] = useState<AdmissionImportPreview | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const download = async () => {
    setBusy(true);
    setMessage('');
    try {
      const result = await downloadPrefilledAdmissionTemplate(apiBase);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(result.blob);
      a.download = result.fileName;
      a.click();
      URL.revokeObjectURL(a.href);
      setMessage(
        `Padrón preparado con ${result.rowCount} alícuotas pendientes. Modifica únicamente las excepciones.`,
      );
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'No se pudo preparar el padrón.');
    } finally {
      setBusy(false);
    }
  };
  const review = async () => {
    if (!csv) return;
    setBusy(true);
    setMessage('');
    try {
      setPreview(await previewAdmissionImport(apiBase, csv));
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'No se pudo revisar.');
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (!preview || !pdf || preview.errorRows) return;
    setBusy(true);
    setMessage('');
    try {
      await confirmAdmissionImport(apiBase, preview.batchId, pdf);
      setOpen(false);
      setPreview(null);
      setCsv(null);
      setPdf(null);
      onDone();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'No se pudo confirmar.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button icon={<FileSpreadsheet />} onClick={() => setOpen(true)}>
        Carga masiva de fechas
      </Button>
      <CanonicalModal
        open={open}
        size="large"
        eyebrow="CARGA AUDITADA"
        title="Fechas históricas por alícuota"
        description="La vista previa no modifica titularidades ni genera obligaciones."
        onClose={() => setOpen(false)}
      >
        <div className="admission-bulk">
          {message ? <InfoPanel tone="danger">{message}</InfoPanel> : null}
          <InfoPanel title="Plantilla multi-entidad">
            Una fila por alícuota. La fecha determina el primer mes cobrable. Todas las filas deben
            ser válidas para confirmar.
          </InfoPanel>
          <Button disabled={busy} onClick={() => void download()}>
            Descargar padrón precargado
          </Button>
          <label>Plantilla completada *</label>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              setCsv(e.target.files?.[0] ?? null);
              setPreview(null);
            }}
          />
          <Button variant="primary" disabled={!csv || busy} onClick={() => void review()}>
            {busy ? 'Revisando…' : 'Generar vista previa'}
          </Button>
          {preview ? (
            <>
              <div className="admission-bulk__summary">
                <strong>{preview.totalRows} filas</strong>
                <StatusBadge tone="success">{preview.validRows} válidas</StatusBadge>
                <StatusBadge tone={preview.errorRows ? 'danger' : 'success'}>
                  {preview.errorRows} con error
                </StatusBadge>
              </div>
              <div className="admission-bulk__table">
                <table>
                  <thead>
                    <tr>
                      <th>Fila</th>
                      <th>Entidad / alícuota</th>
                      <th>Titular</th>
                      <th>Ingreso</th>
                      <th>Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.line}>
                        <td>{r.line}</td>
                        <td>
                          {r.entityCode} · {r.slotNumber}
                        </td>
                        <td>{r.member_name ?? 'No identificado'}</td>
                        <td>{r.joinedOn}</td>
                        <td>{r.valid ? 'Lista' : r.issues.join(' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label>PDF de respaldo del lote *</label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setPdf(e.target.files?.[0] ?? null)}
              />
              <InfoPanel title="Confirmación segura">
                Esta operación registra o corrige fechas con auditoría. No crea ni recalcula
                obligaciones.
              </InfoPanel>
              <Button
                variant="primary"
                disabled={busy || preview.errorRows > 0 || !pdf}
                onClick={() => void confirm()}
              >
                {busy ? 'Confirmando…' : 'Confirmar fechas validadas'}
              </Button>
            </>
          ) : null}
        </div>
      </CanonicalModal>
    </>
  );
}
export function RemainingAdmissionBulkVerification({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false),
    [preview, setPreview] = useState<RemainingAdmissionVerification | null>(null),
    [reference, setReference] = useState(''),
    [reason, setReason] = useState(''),
    [pdf, setPdf] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const show = async () => {
    setOpen(true);
    setBusy(true);
    setMessage('');
    try {
      setPreview(await getRemainingAdmissionVerification(apiBase));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No se pudo preparar la vista previa.');
    } finally {
      setBusy(false);
    }
  };
  const ready = Boolean(
    preview?.count &&
    reference.trim().length >= 3 &&
    reason.trim().length >= 10 &&
    pdf?.type === 'application/pdf',
  );
  const confirm = async () => {
    if (!ready || !pdf) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await verifyRemainingAdmissions(apiBase, { reference, reason, file: pdf });
      setMessage(`${result.verifiedRows} fechas quedaron verificadas.`);
      setPreview({ count: 0, items: [], writesPerformed: false });
      onDone();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No se pudo confirmar el lote.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button onClick={() => void show()}>Verificar fechas restantes</Button>
      <CanonicalModal
        open={open}
        size="large"
        eyebrow="VERIFICACIÓN EN SERIE"
        title="Verificar fechas provisionales restantes"
        description="Úsalo después de corregir individualmente todas las excepciones."
        onClose={() => setOpen(false)}
      >
        <div className="admission-bulk">
          {message ? <InfoPanel>{message}</InfoPanel> : null}
          {busy && !preview ? (
            <InfoPanel title="Preparando vista previa">
              Consultando alícuotas provisionales…
            </InfoPanel>
          ) : null}
          {preview ? (
            <>
              <div className="admission-bulk__summary">
                <strong>{preview.count} fechas provisionales</strong>
                <StatusBadge tone={preview.count ? 'warning' : 'success'}>
                  {preview.count ? 'Pendientes' : 'Ninguna pendiente'}
                </StatusBadge>
              </div>
              {preview.count ? (
                <>
                  <InfoPanel title="Control previo">
                    Se conservarán las fechas actuales. Solo cambiarán de Provisional a Verificada,
                    con una única evidencia de lote y auditoría individual.
                  </InfoPanel>
                  <div className="admission-bulk__table">
                    <table>
                      <thead>
                        <tr>
                          <th>Entidad / alícuota</th>
                          <th>Titular</th>
                          <th>Fecha conservada</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.items.map((item) => (
                          <tr key={item.admissionId}>
                            <td>
                              {item.entityCode} · {item.slotNumber}
                            </td>
                            <td>{item.memberName}</td>
                            <td>{item.joinedOn}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {preview.count > preview.items.length ? (
                    <small>
                      Se muestran las primeras {preview.items.length} de {preview.count} alícuotas.
                    </small>
                  ) : null}
                  <label>Referencia general *</label>
                  <input
                    value={reference}
                    maxLength={200}
                    onChange={(event) => setReference(event.target.value)}
                    placeholder="Ej. Certificación del padrón institucional"
                  />
                  <label>Motivo de la verificación *</label>
                  <textarea
                    rows={3}
                    maxLength={500}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Explica por qué las fechas restantes se consideran verificadas"
                  />
                  <label>PDF de respaldo del lote *</label>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(event) => setPdf(event.target.files?.[0] ?? null)}
                  />
                  <Button
                    variant="primary"
                    disabled={!ready || busy}
                    onClick={() => void confirm()}
                  >
                    {busy ? 'Verificando…' : `Verificar ${preview.count} fechas restantes`}
                  </Button>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </CanonicalModal>
    </>
  );
}
